<template>
  <div class="grid-headline">
    <div class="select-columns-container">
      <b-dropdown aria-role="menu" trap-focus :mobile-modal="false" @active-change="onColumnsDropdownActiveChange">
        <template #trigger>
          <button class="is-transparent" aria-haspopup="true" aria-controls="dropdown-menu" :class="{ active: showSelectColumns }" @click="showSelectColumns = !showSelectColumns">
            <span class="button-text-w">
              <span class="text">{{ $t("Grid.ColumnsSelect") }}</span>
            </span>
            <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down"><icon-caret-down /></icon-base>
          </button>
        </template>

        <b-dropdown-item aria-role="menu-item" :focusable="false" custom paddingless>
          <div v-for="(item, index) in columns" :key="index">
            <b-field :class-name="index" v-if="!item.forbidden">
              <b-checkbox v-model="columns[index].visible" :native-value="item.id" @change.native="onColumnSelect(columns[index], $event)">
                {{ item.label }}
              </b-checkbox>
            </b-field>
          </div>
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <b-button class="is-icon" @click="goBack()">
      <span>{{ $t("Back") }}</span>
    </b-button>
  </div>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { DataSourceResult } from "@/models/DataSourceResult";
import { GridColumn } from "@/models/GridColumn";
import { Result } from "@/models/Result";
import { TableSettings, TableSettingsState, TableSettingsStateColumn } from "@/models/TableSettings";
import HelperService from "@/services/HelperService";
import ApiService from "@/services/api/ApiService";
import { CampaignsPayload } from "@/services/api/CampaignsApiService";
import IconBase from "../IconBase.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import GridTableSettingsEn from "../../data/grid-defaultsettings-en.json";
import GridTableSettingsHe from "../../data/grid-defaultsettings-he.json";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});

const namespace = "campaignsModule";

@Component({
  components: {
    IconBase,
    IconCaretDown,
  },
})
export default class CampaignsKeywordsGridHeadline extends Vue {
  @Prop() readonly columns!: GridColumn[];

  //#region Vuex getters and actions

  @Getter("campaigns", { namespace }) campaigns!: DataSourceResult;
  @Getter("campaignsPayload", { namespace }) campaignsPayload!: CampaignsPayload;
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings[];
  @Action("getCampaigns", { namespace }) getCampaigns!: (payload: CampaignsPayload) => Promise<DataSourceResult>;
  @Action("setTableSettings", { namespace }) setTableSettings!: (tableSettings: TableSettings[] | undefined) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  gridId = "ad_groups__keywords";
  showSelectColumns = false;

  //#endregion Properties


  //#region Getters

  get keywordsTableSettings(): TableSettings {
    let settings: TableSettings | undefined = this.tableSettings.find((i: TableSettings) => i.GridId === this.gridId);
    if (!settings) {
      let settingsState: TableSettingsState = GridTableSettingsHe;
      switch (i18n.locale) {
        case "en-US":
          settingsState = GridTableSettingsEn;
          break;
        case "he-IL":
          settingsState = GridTableSettingsHe;
          break;
      }
      settings = {
        Culture: i18n.locale,
        GridId: this.gridId,
        State: JSON.stringify(settingsState),
        StateObject: settingsState,
        Url: location.pathname
      };
      this.tableSettings.push(settings);
      this.setTableSettings(this.tableSettings);
    }
    return settings;
  }

  //#endregion Getters


  //#region Handlers

  onColumnsDropdownActiveChange(show: boolean) {
    if (show === false) {
      ApiService.saveTableSettings({
        culture: this.keywordsTableSettings.Culture,
        gridId: this.keywordsTableSettings.GridId,
        optionsJson: JSON.stringify(this.keywordsTableSettings.StateObject),
        url: this.keywordsTableSettings.Url
      }).then((result: Result) => {
        if (!result.Success) {
          HelperService.showError(result.Message);
        }
      });
    }
  }

  onColumnSelect(column: GridColumn, e: Event) {
    column.visible = (e.target as HTMLInputElement).checked;
    const selectedCampaignGridColumnsIdsNew = this.columns.filter((i: GridColumn) => i.visible)
          .map((i: GridColumn) => i.id);
    this.$emit("select-columns", selectedCampaignGridColumnsIdsNew);
    column.sortable = column.visible;

    let settingsColumn: TableSettingsStateColumn | undefined = this.keywordsTableSettings.StateObject.columns.find(this.findTableSettingsColumn(column));
    if (settingsColumn) {
      HelperService.onColumnSelectInitSettingsColumn(settingsColumn, column);
    } else {
      settingsColumn = HelperService.createTableSettingsColumn(column);
      this.keywordsTableSettings.StateObject.columns.push(settingsColumn);
    }
    settingsColumn = this.keywordsTableSettings.StateObject.dataSource.fields.find(this.findTableSettingsColumn(column));
    if (settingsColumn) {
      HelperService.onColumnSelectInitSettingsColumn(settingsColumn, column);
    } else {
      settingsColumn = HelperService.createTableSettingsColumn(column);
      this.keywordsTableSettings.StateObject.dataSource.fields.push(settingsColumn);
    }
  }

  //#endregion Handlers


  //#region Private methods

  private findTableSettingsColumn(column: GridColumn): (settingsColumn: TableSettingsStateColumn) => boolean {
    return (settingsColumn: TableSettingsStateColumn) => {
      let result = settingsColumn.field && settingsColumn.field === column.name
            || !settingsColumn.field && settingsColumn.title.toLowerCase() === column.label.toLowerCase();
      return result; 
    }
  }

  goBack() {
    this.$emit("keywords-close");
  }

  //#endregion Private methods
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.grid-headline {
  align-items: center;
  border-bottom: 1px solid #d3d6e2;
  display: flex;
  justify-content: space-between;
  padding: 18px 21px;
  position: relative;
  z-index: 1;

  @media (max-width: 991px) {
    padding: 14px 10px 14px 15px;
  }
  @media (max-width: 399px) {
    justify-content: space-between;
  }
  @media (max-width: 359px) {
    display: block;
    padding: 0;
  }
  .select-columns-container {
    padding-left: 20px;
    position: relative;
    @media (max-width: 991px) {
      padding-left: 15px;
    }
    @media (max-width: 399px) {
      padding-left: 5px;
    }
    @media (max-width: 359px) {
      padding: 15px 15px;
      border-bottom: 1px solid $lightd;
    }
    .button-text-w {
      margin-left: 11px;
    }
    .dropdown-menu {
      top: 46px;
      right: -22px;
      @media (max-width: 991px) {
        top: 43px;
        right: -11px;
      }
      @media (max-width: 359px) {
        top: 39px;
        right: -16px;
      }

      .dropdown-content {
        border: 1px solid #d3d6e2;
        box-shadow: 0px 0px 4px #d3d6e2;
        width: 529px;
        padding: 25px 25px 10px;
        @media (max-width: 991px) {
          border-radius: 0;
        }
        @media (max-width: 575px) {
          width: 384px;
          padding: 15px 15px 0;
        }
        @media (max-width: 399px) {
          width: 345px;
        }
        @media (max-width: 360px) {
          width: 330px;
        }

        @media (max-width: 359px) {
          width: 290px;
        }
      }
      .dropdown-item {
        columns: 3;
        @media (max-width: 575px) {
          columns: 2;
        }
        .field {
          margin-bottom: 14px;
          @media (max-width: 575px) {
            margin-bottom: 12px;
          }
        }
      }
    }
  }
  .border {
    display: block;
    height: 33px;
    width: 1px;
    background: #dcdcdc;
    @media (max-width: 991px) {
      height: 35px;
    }
    @media (max-width: 359px) {
      display: none;
    }
  }
}

html[dir="ltr"] {
  .grid-headline {
    @media (max-width: 991px) {
      padding: 14px 15px 14px 10px;
    }
    .select-columns-container {
      padding: 0 20px 0 0;
      @media (max-width: 991px) {
        padding: 0 15px 0 0;
      }
      @media (max-width: 399px) {
        padding: 0 5px 0 0;
      }
      @media (max-width: 359px) {
        padding: 15px 15px;
      }
      .button-text-w {
        margin: 0 11px 0 0;
      }
    }
  }
}
</style>
